import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { adminMaintenanceMessagesInitialState, AdminMaintenanceMessagesState } from './maintenanceMessages.state';
import { StatusType } from 'App/types/requestStatus';
import {
	GetMessagesResponse,
	GetMessageResponse,
	UpdateMessageResponse,
} from 'App/api/endpoints/maintenanceMessages/responses';
import { GetMessagesRequest } from 'App/api/endpoints/maintenanceMessages/requests';

const { FAILED, LOADING, SUCCESS } = StatusType;

export const adminMaintenanceMessagesSlice = createSlice({
	name: 'admin-maintenace-messages',
	initialState: adminMaintenanceMessagesInitialState,
	reducers: {
		getMessagesStart: (state: AdminMaintenanceMessagesState) => {
			state.status.getMessages = LOADING;
			state.messages = adminMaintenanceMessagesInitialState.messages;
		},
		getMessagesSuccess(state: AdminMaintenanceMessagesState, action: PayloadAction<GetMessagesResponse>) {
			state.status.getMessages = SUCCESS;
			const { messages, ...pagination } = action.payload;
			state.messages = action.payload.messages;
			state.getMessagesParams = pagination;
		},
		getMessagesFailure(state: AdminMaintenanceMessagesState) {
			state.status.getMessages = FAILED;
		},

		getMessageStart: (state: AdminMaintenanceMessagesState) => {
			state.status.getMessage = LOADING;
			state.selectedMessage = adminMaintenanceMessagesInitialState.selectedMessage;
		},
		getMessageSuccess: (state: AdminMaintenanceMessagesState, action: PayloadAction<GetMessageResponse>) => {
			state.status.getMessage = SUCCESS;
			state.selectedMessage = action.payload;
		},
		getMessageFailure: (state: AdminMaintenanceMessagesState) => {
			state.status.getMessage = FAILED;
		},

		deleteMessageStart: (state: AdminMaintenanceMessagesState) => {
			state.status.deleteMessage = LOADING;
		},
		deleteMessageSuccess: (state: AdminMaintenanceMessagesState, action: PayloadAction<number>) => {
			state.status.deleteMessage = SUCCESS;
			state.messages = state.messages.filter((m) => m.id !== action.payload);
		},
		deleteMessageFailure: (state: AdminMaintenanceMessagesState) => {
			state.status.deleteMessage = FAILED;
		},

		createMessageStart: (state: AdminMaintenanceMessagesState) => {
			state.status.createMessage = LOADING;
		},
		createMessageSuccess: (state: AdminMaintenanceMessagesState) => {
			state.status.createMessage = SUCCESS;
		},
		createMessageFailure: (state: AdminMaintenanceMessagesState) => {
			state.status.createMessage = FAILED;
		},

		updateMessageStart: (state: AdminMaintenanceMessagesState) => {
			state.status.updateMessage = LOADING;
		},
		updateMessageSuccess: (state: AdminMaintenanceMessagesState, action: PayloadAction<UpdateMessageResponse>) => {
			state.status.updateMessage = SUCCESS;
			const message = state.messages.find((u) => u.id === action.payload.id);

			if (message) {
				const { startDateInUtc: startDate, endDateInUtc: endDate, description } = action.payload;

				message.startDateInUtc = startDate;
				message.endDateInUtc = endDate;
				message.description = description;
			}
		},
		updateMessageFailure: (state: AdminMaintenanceMessagesState) => {
			state.status.updateMessage = FAILED;
		},

		updateGetMessagesParams: (state: AdminMaintenanceMessagesState, action: PayloadAction<GetMessagesRequest>) => {
			state.getMessagesParams = action.payload;
		},

		cleanUpMessageStatus: (state: AdminMaintenanceMessagesState) => {
			state.status = adminMaintenanceMessagesInitialState.status;
		},

		cleanUpMessagesTable: (state: AdminMaintenanceMessagesState) => {
			state.getMessagesParams = adminMaintenanceMessagesInitialState.getMessagesParams;
			state.messages = adminMaintenanceMessagesInitialState.messages;
			state.status.getMessages = adminMaintenanceMessagesInitialState.status.getMessages;
		},

		cleanUpSelectedMessage: (state: AdminMaintenanceMessagesState) => {
			state.selectedMessage = adminMaintenanceMessagesInitialState.selectedMessage;
		},
	},
});

export default adminMaintenanceMessagesSlice;

export const {
	getMessagesStart,
	getMessagesSuccess,
	getMessagesFailure,

	getMessageStart,
	getMessageSuccess,
	getMessageFailure,

	deleteMessageStart,
	deleteMessageSuccess,
	deleteMessageFailure,

	createMessageStart,
	createMessageSuccess,
	createMessageFailure,

	updateMessageStart,
	updateMessageSuccess,
	updateMessageFailure,

	updateGetMessagesParams,

	cleanUpMessageStatus,
	cleanUpSelectedMessage,
	cleanUpMessagesTable,
} = adminMaintenanceMessagesSlice.actions;
